import React from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { trpcClient } from '@/lib/trpc';
import { DesignForm, type DesignFormData } from './DesignForm';

export default function DesignEdit() {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as any;
  const designId = params.designId;
  const queryClient = useQueryClient();

  const { data: design, isLoading, error } = useQuery({
    queryKey: ['design', designId],
    queryFn: async () => (trpcClient as any).designs.getById.query({ id: designId }),
    enabled: !!designId,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: DesignFormData) => {
      return (trpcClient as any).designs.update.mutate({
        id: designId,
        data: {
          designType: data.designType,
          quantity: data.quantity,
          description: data.description,
          assignedTo: data.assignedTo || undefined,
          status: data.status,
          filePaths: data.filePaths ? [data.filePaths] : undefined,
        },
      });
    },
    onSuccess: () => {
      toast.success('Đã cập nhật hồ sơ thiết kế thành công');
      queryClient.invalidateQueries({ queryKey: ['design', designId] });
      queryClient.invalidateQueries({ queryKey: ['designs'] });
      navigate({ to: `/designs/${designId}` as any });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Không thể cập nhật hồ sơ thiết kế');
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-slate-500 text-sm">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mb-2" />
        Đang tải thông tin hồ sơ thiết kế...
      </div>
    );
  }

  if (error || !design) {
    return (
      <div className="p-8 text-center text-red-500">
        Không tìm thấy hồ sơ thiết kế #{designId}
      </div>
    );
  }

  return (
    <DesignForm
      mode="edit"
      initialData={{
        id: design.id,
        productId: design.productId,
        designType: design.designType,
        quantity: design.quantity || 1,
        description: design.description || '',
        status: design.status,
        assignedTo: design.assignedTo || '',
        filePaths: Array.isArray(design.filePaths) ? design.filePaths[0] : design.filePaths,
      }}
      isSubmitting={updateMutation.isPending}
      onSubmit={async (data) => {
        await updateMutation.mutateAsync(data);
      }}
      onCancel={() => navigate({ to: `/designs/${designId}` as any })}
    />
  );
}
